import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./primitives";

const NewsletterInline = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email, source: "footer" });
      toast.success(data.message);
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="footer-newsletter-form" className="flex flex-col sm:flex-row gap-3">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email address"
        data-testid="footer-newsletter-input"
        className="flex-1 bg-transparent border-b border-line py-3 text-ink placeholder:text-ink-soft/60 focus:outline-none focus:border-gold transition-colors"
      />
      <Button type="submit" data-testid="footer-newsletter-submit" disabled={loading}>
        {loading ? "…" : "Subscribe"}
      </Button>
    </form>
  );
};

const cols = [
  { title: "Explore", links: [["About", "/about"], ["Our Values", "/values"], ["Manifesto", "/manifesto"], ["Constitution", "/constitution"]] },
  { title: "Engage", links: [["Services", "/services"], ["Book Appointment", "/book"], ["Support the Vision", "/support"], ["FAQ", "/faq"]] },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-surface border-t border-line">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Link to="/" className="font-serif text-3xl text-ink">
            Glam <span className="italic text-gold">Wagon</span>
          </Link>
          <p className="mt-5 text-ink-soft max-w-sm leading-relaxed">
            A values-driven beauty company proving that integrity and profit can rise together.
            This is only the beginning.
          </p>
          <div className="mt-7">
            <p className="eyebrow mb-3">Join the movement</p>
            <NewsletterInline />
          </div>
        </div>
        {cols.map((c) => (
          <div key={c.title} className="md:col-span-2">
            <p className="eyebrow mb-5">{c.title}</p>
            <ul className="space-y-3">
              {c.links.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="text-ink-soft hover:text-gold transition-colors" data-testid={`footer-link-${label.toLowerCase().replace(/\s/g, "-")}`}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div className="md:col-span-3">
          <p className="eyebrow mb-5">Reach us</p>
          <a href="mailto:hello@glamwagon.co" className="text-ink-soft hover:text-gold transition-colors block mb-4">
            hello@glamwagon.co
          </a>
          <div className="flex gap-3">
            {[Instagram, Facebook, Mail].map((Icon, i) => (
              <a
                key={Icon.displayName || i}
                href="#"
                aria-label="social"
                data-testid={`social-link-${i}`}
                className="p-2.5 rounded-full border border-line text-ink hover:border-gold hover:text-gold transition-colors"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-16 pt-8 border-t border-line flex flex-col sm:flex-row justify-between gap-4 text-sm text-ink-soft">
        <span>© {new Date().getFullYear()} Glam Wagon. Built on values before profit.</span>
        <span>Made with care for the communities we serve.</span>
      </div>
    </div>
  </footer>
);
